/* eslint-disable react/prop-types */
import {
  Button,
  ButtonGroup,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Icon,
  IconButton,
  InputLabel,
  ListItemButton,
  ListItemText,
  Select,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import PropTypes from 'prop-types';
import * as React from 'react';

import {
  DateRange,
  ExpandLess,
  ExpandMore,
  RestartAlt,
} from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useSelector } from 'react-redux';
import TaskDefinitionAutocomplete from '../../../../../components/Autocompletes/TaskDefinition';

import BarnHorsesAutocomplete from '../../../../../components/Autocompletes/BarnHorses';
import BarnUsersAutocomplete from '../../../../../components/Autocompletes/BarnUsers';
import TagsAutocomplete from '../../../../../components/Autocompletes/Tags';
import { getStatusItemSecondaryText } from '../../../../../components/StatusAssignment';
import {
  blueGrey,
  primary,
} from '../../../../../utils/brandColors';
import {
  THIS_MONTH_TYPE,
  THIS_WEEK_TYPE,
  THIS_YEAR_TYPE,
  TODAY_TYPE,
} from '../../../../../utils/dashboardHelper';
import {
  fromPickerValue,
  toMomentValue,
} from '../../../../../utils/dateHelper';
import theme from '../../../../../utils/theme';
import useDebounce from '../../../../../utils/useDebounce';

const getTagConditions = (barnHorses, filterTags, barnMetadata) => {
  let tagConditions = [];

  if (barnHorses.length > 0) {
    tagConditions = [barnHorses.map((barnHorse) => parseInt(barnHorse.pingumTagId, 10))];
  }

  // add filter tags if present
  if (filterTags.length > 0) {
    tagConditions = [...tagConditions, filterTags.map((t) => t.id)];
  }

  return [...tagConditions, [barnMetadata.eventTaskTypeTagId, barnMetadata.horseTaskTypeTagId]];
};

export const getSelectedNumberForType
  = (tagType, tags) => {
    const selected = tags.filter((tag) => tag.tagType.id === tagType.id && tag.selected).length;
    return `${selected === 0
      ? 'None'
      : selected.toString()} selected`;
  };

export const addParentToRootTagsAndSetSelected = (tags, tagConditions) => {
  const data = [];
  for (let i = 0; i < tags.length; i += 1) {
    const newTag = tags[i];

    // check if a tag is selected and what group it might be in
    let found = false;
    for (let group = 0; group < tagConditions.length; group += 1) {
      for (let j = 0; j < tagConditions[group].length; j += 1) {
        // is this tag in a group? if yes then select it and assign group index
        if (tagConditions[group][j] === newTag.id) {
          found = true;
          newTag.tagGroupIndex = group;
        } else {
          // if a tag in this group has same tag type then we can set the group index
          const foundTag = tags.filter((tag) => tag.id === tagConditions[group][j]);
          if (foundTag.length > 0 && foundTag[0].tagType.id === newTag.tagType.id) {
            newTag.tagGroupIndex = group;
          }
        }
      }
    }

    if (!found) {
      newTag.selected = false;
    } else {
      newTag.selected = true;
    }

    if (!newTag.parentTag) {
      newTag.parentTag = { id: 0 };
    }
    data.push(newTag);
  }

  return data.sort((a, b) => a.name - b.name);
};

const FilterComponent = ({
  filter,
  tags: propTags,
  isFetchingEvents,
  onClose,
  includeToday,
  includeThisWeek,
  includeThisMonth,
  includeThisYear,
  onFilter,
  rounded,
}) => {
  // eslint-disable-next-line no-unused-vars
  const smallDown = useMediaQuery(theme.breakpoints.down('sm'));
  const mediumDown = useMediaQuery(theme.breakpoints.down('md'));

  const [updateFilter, setUpdateFiltler] = React.useState(filter);
  const [openFilter, setOpenFilter] = React.useState(false);
  const [tags, setTags] = React.useState([]);
  const [selectedBarnHorses, setSelectedBarnHorses] = React.useState([])

  const eventTemplates = useSelector((state) => state.eventTemplate.eventTemplates)
  const barnUsersMap = useSelector((state) => state.barn.barnUsersMap)
  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap)
  const selectedBarn = useSelector((state) => state.barn.selectedBarn)
  const statuses = useSelector((state) => state.status.statuses)

  const debouncedFilter = useDebounce(updateFilter, 750);

  React.useEffect(() => {
    setUpdateFiltler(filter);
    if (filter.tagConditions) {
      for (let i = 0; i < filter.tagConditions.length; i += 1) {
        // if there are values and the first is a tag, then add to tags list
        // if (filter.tagConditions[i].length > 0 && filter.tagConditions[i][0]) {

        // }
        // if there are values and the first is a horse, then add to horse list
        if (filter.tagConditions[i].length > 0 && barnHorsesMap[filter.tagConditions[i][0]]) {
          const filterHorses = filter.tagConditions[i]
            .map((id) => ((barnHorsesMap[id] && barnHorsesMap[id][0]) || { id: -1 }))
            .filter((bh) => bh.id !== -1)
          setSelectedBarnHorses(filterHorses)
        }
        if (filter.tagConditions[i].length > 0 && !barnHorsesMap[filter.tagConditions[i][0]]
          && (filter.tagConditions[i][0] !== selectedBarn.barnMetadata.horseTaskTypeTagId
            && filter.tagConditions[i][0] !== selectedBarn.barnMetadata.eventTaskTypeTagId)) {
          const filterTags = filter.tagConditions[i]
            .map((id) => (propTags.find((t) => t.id === id) || { id: -1 }))
            .filter((bh) => bh.id !== -1)
          setTags(filterTags)
        }
      }
    }
  }, [filter]);

  React.useEffect(() => {
    onFilter(debouncedFilter)
  }, [debouncedFilter])

  const handleUpdateFilter = (data, field) => {
    const updatedFilter = filter;
    updatedFilter[field] = data;
    setUpdateFiltler(JSON.parse(JSON.stringify(updatedFilter)));
  };

  const handleTagsChange = (newBarnHorses, newTags) => {
    handleUpdateFilter(getTagConditions(newBarnHorses, newTags, selectedBarn.barnMetadata), 'tagConditions')
  }

  const handleToggleArchived = (event) => {
    const { target: { checked: includeArchived } } = event;
    handleUpdateFilter(includeArchived, 'includeArchived');
  }

  const handleToggleComplete = (event) => {
    const { target: { checked: includeComplete } } = event;
    handleUpdateFilter(includeComplete, 'includeComplete');
  }

  const handleStartDateChange = (newDateValue) => {
    handleUpdateFilter(fromPickerValue(newDateValue), 'startDate');
  }

  const handleEndDateChange = (newDateValue) => {
    handleUpdateFilter(fromPickerValue(newDateValue), 'endDate');
  }

  const selectDateRange = (selectedDateType) => {
    handleUpdateFilter(null, 'endDate');
    handleUpdateFilter(null, 'startDate');
    if (selectedDateType === TODAY_TYPE) {
      handleUpdateFilter(TODAY_TYPE, 'presetDateFilter');
    } else if (selectedDateType === THIS_WEEK_TYPE) {
      handleUpdateFilter(THIS_WEEK_TYPE, 'presetDateFilter');
    } else if (selectedDateType === THIS_MONTH_TYPE) {
      handleUpdateFilter(THIS_MONTH_TYPE, 'presetDateFilter');
    } else if (selectedDateType === THIS_YEAR_TYPE) {
      handleUpdateFilter(THIS_YEAR_TYPE, 'presetDateFilter');
    } else {
      handleUpdateFilter(null, 'presetDateFilter');
    }
  }

  return (
    <AppBar
      position="relative"
      color="inherit"
      sx={{
        borderRadius: rounded
          ? '8px'
          : 0,
      }}
      elevation={2}
    >
      <Container
        maxWidth="100%"
        sx={{
          pt: 1,
          pb: 0.5,
          height: (mediumDown && !openFilter)
            ? 60
            : '100%',
          overflow: 'hidden',
        }}
      >
        <Toolbar disableGutters variant="dense">
          <Box sx={{ width: '100%' }}>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                width: '100%',
              }}
            >
              <Grid container alignItems="center" spacing={2}>

                <Grid sx={{ minWidth: 200 }}>
                  <BarnHorsesAutocomplete
                    multiple
                    value={selectedBarnHorses}
                    onSelect={(e, newHorses) => { setSelectedBarnHorses(newHorses); handleTagsChange(newHorses, tags); }}
                    limitTags={1}
                    barnHorses={undefined}
                    size="small"
                    label="Filter by horse"
                    showAdornment
                  />
                </Grid>

                <Grid>
                  <Box
                    sx={{ width: 200 }}
                  >
                    <TaskDefinitionAutocomplete
                      label="Tasks"
                      multiple
                      size="small"
                      limitTags={1}
                      eventTemplates={null} // needs to be null so component uses all templates
                      value={((updateFilter && updateFilter.eventTemplateIds) || [])
                        .map((id) => (eventTemplates.find((et) => et.id === id) || {
                          id,
                          name: 'Unknown',
                        }))}
                      onSelect={(e, taskDefinitions) => {
                        handleUpdateFilter(taskDefinitions.map((et) => et.id), 'eventTemplateIds');
                      }}
                      includeEventTaskTypeTagId={false}
                      icon
                    />
                  </Box>
                </Grid>
                <Grid>
                  <Tooltip title="Show Completed Tasks">
                    <FormControlLabel
                      sx={{ m: 0 }}
                      labelPlacement="end"
                      control={(
                        <Checkbox
                          checked={filter.includeComplete}
                          onChange={handleToggleComplete}
                          value="includeComplete"
                          color="primary"
                        />
                      )}
                      label="Show Completed"
                    />
                  </Tooltip>
                </Grid>
              </Grid>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    borderLeft: `1px solid ${'var(--blueGrey-unknown)'}`,
                    pl: 1,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  style={{ '--blueGrey-unknown': blueGrey[100] }}
                >
                  {
                    smallDown
                      ? (
                        <IconButton
                          onClick={(e) => { e.stopPropagation(); setOpenFilter(!openFilter); }}
                        >
                          {(openFilter
                            ? <ExpandLess />
                            : <ExpandMore />)}
                        </IconButton>
                      )
                      : (

                        <Button
                          sx={{
                            mr: 2,
                            width: 150,
                          }}
                          endIcon={(openFilter
                            ? <ExpandLess />
                            : <ExpandMore />)}
                          onClick={(e) => { e.stopPropagation(); setOpenFilter(!openFilter); }}
                        >
                          {openFilter
                            ? 'Hide'
                            : 'More Options'}
                        </Button>
                      )
                  }
                  {isFetchingEvents
                    ? (
                      <CircularProgress size={20} />
                    )
                    : (
                      <Tooltip title="Reset search">
                        <IconButton
                          onClick={() => {
                            setTags([]);
                            setSelectedBarnHorses([]);
                            onClose();
                          }}
                          color="secondary"
                          name="cancel-button"
                          size="small"
                          variant="outlined"
                        >
                          <RestartAlt />
                        </IconButton>
                      </Tooltip>
                    )}
                </Box>
              </Box>

            </Box>
            <Collapse in={openFilter}>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={1}>

                <Grid>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      borderRight: `1px solid ${'var(--blueGrey-unknown)'}`,
                      pr: 1,
                    }}
                    style={{ '--blueGrey-unknown': blueGrey[100] }}
                  >
                    <Tooltip title="Search for tasks started in date range">
                      <DateRange sx={{ mr: 0.5 }} color="primary" />
                    </Tooltip>
                    <ButtonGroup>
                      {
                        includeToday && (
                          <Button
                            variant={filter.presetDateFilter === TODAY_TYPE
                              ? 'contained'
                              : 'outlined'}
                            size="small"
                            color="primary"
                            onClick={() => selectDateRange(TODAY_TYPE)}
                          >
                            Today
                          </Button>
                        )
                      }
                      {
                        includeThisWeek && (
                          <Button
                            variant={filter.presetDateFilter === THIS_WEEK_TYPE
                              ? 'contained'
                              : 'outlined'}
                            size="small"
                            color="primary"
                            onClick={() => selectDateRange(THIS_WEEK_TYPE)}
                          >
                            This Week
                          </Button>
                        )
                      }

                      {
                        includeThisMonth && (
                          <Button
                            variant={
                              filter.presetDateFilter === THIS_MONTH_TYPE
                                ? 'contained'
                                : 'outlined'
                            }
                            size="small"
                            color="primary"
                            onClick={() => selectDateRange(THIS_MONTH_TYPE)}
                          >
                            This Month
                          </Button>
                        )
                      }
                      {
                        includeThisYear && (
                          <Button
                            variant={
                              filter.presetDateFilter === THIS_YEAR_TYPE
                                ? 'contained'
                                : 'outlined'
                            }
                            size="small"
                            color="primary"
                            onClick={() => selectDateRange(THIS_YEAR_TYPE)}
                          >
                            This Year
                          </Button>
                        )
                      }
                      <Button
                        variant={filter.presetDateFilter === null
                          ? 'contained'
                          : 'outlined'}
                        size="small"
                        color="primary"
                        onClick={() => selectDateRange(null)}
                      >
                        Custom
                      </Button>
                    </ButtonGroup>
                    {
                      filter.presetDateFilter === null
                      && (
                        <div>
                          <LocalizationProvider dateAdapter={AdapterMoment}>
                            <FormControl sx={{ ml: 1 }}>
                              <DatePicker
                                label="Start Date"
                                value={toMomentValue(filter.startDate)}
                                onChange={handleStartDateChange}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    sx: { width: '150px' },
                                    margin: 'dense',
                                    required: true,
                                  },
                                }}
                              />
                            </FormControl>
                            <FormControl sx={{ ml: 1 }}>
                              <DatePicker
                                label="End Date"
                                value={toMomentValue(filter.endDate)}
                                onChange={handleEndDateChange}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    sx: { width: '150px' },
                                    margin: 'dense',
                                    required: false,
                                  },
                                }}
                              />
                            </FormControl>
                          </LocalizationProvider>
                        </div>
                      )
                    }
                  </Box>
                </Grid>
                <Grid sx={{ minWidth: 200 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="status-name-label">Task Status</InputLabel>
                    <Select
                      fullWidth
                      labelId="status-name-label"
                      id="status-multiple-name"
                      label="Task Status"
                      multiple
                      value={filter.statusIds}
                      onChange={(e) => handleUpdateFilter(e.target.value, 'statusIds')}
                      renderValue={(selected) => selected.map((s) => statuses.find((st) => st.id === s).name).join(', ')}
                    >
                      {
                        statuses.sort((a, b) => a.statusOrder - b.statusOrder).map((status) => (
                          <ListItemButton
                            dense
                            key={`status-${status.id}`}
                            value={status.id}
                            selected={filter.statusIds.indexOf(status.id) >= 0}
                          >
                            <Icon
                              sx={{
                                mr: 1,
                                color: primary[500],
                              }}
                            >{status.icon}
                            </Icon>
                            <ListItemText
                              primary={status.name}
                              secondaryTypographyProps={{ component: 'div' }}
                              secondary={getStatusItemSecondaryText(status, {})}
                            />
                          </ListItemButton>
                        ))
                      }
                    </Select>
                  </FormControl>
                </Grid>
                <Grid sx={{ minWidth: 200 }}>
                  <TagsAutocomplete
                    onSelect={(e, value) => { setTags(value); handleTagsChange(selectedBarnHorses, value); }}
                    value={tags}
                    size="small"
                    editMode={false}
                    limitTags={1}
                    label="Filter by tag"
                  />
                </Grid>
                <Grid>
                  <Box
                    sx={{ width: 200 }}
                  >
                    <BarnUsersAutocomplete
                      label="Assigned User"
                      multiple={false}
                      value={(updateFilter && updateFilter.assignmentAuth0UserId && barnUsersMap[updateFilter.assignmentAuth0UserId]
                        && barnUsersMap[updateFilter.assignmentAuth0UserId][0]) || null}
                      onSelect={(e, newBarnUser) => {
                        const value = (newBarnUser && (newBarnUser.auth0UserId || ((newBarnUser.email || newBarnUser.userFullName) && `${newBarnUser.email || 'no_email'};${newBarnUser.userFullName || 'no_userFullName'}`)))
                          || null
                        handleUpdateFilter(value, 'assignmentAuth0UserId')
                      }}
                      limitTags={1}
                    />
                  </Box>
                </Grid>
                <Grid>
                  <Box
                    sx={{ width: 200 }}
                  >
                    <BarnUsersAutocomplete
                      label="Participant"
                      multiple={false}
                      value={(updateFilter && updateFilter.scheduleAssignmentAuth0UserId && barnUsersMap[updateFilter.scheduleAssignmentAuth0UserId]
                        && barnUsersMap[updateFilter.scheduleAssignmentAuth0UserId][0]) || null}
                      onSelect={(e, newBarnUser) => handleUpdateFilter((newBarnUser && (newBarnUser.auth0UserId || newBarnUser.email || newBarnUser.userFullName)) || null, 'scheduleAssignmentAuth0UserId')}
                      limitTags={1}
                    />
                  </Box>
                </Grid>

                <Grid>
                  <Grid>
                    <FormGroup
                      row
                      sx={{ borderLeft: `1px solid ${'var(--blueGrey-unknown)'}` }}
                      style={{ '--blueGrey-unknown': blueGrey[100] }}
                    >
                      <Tooltip title="Include Completed Tasks">
                        <FormControlLabel
                          sx={{ m: 0 }}
                          labelPlacement="end"
                          control={(
                            <Checkbox
                              checked={filter.includeComplete}
                              onChange={handleToggleComplete}
                              value="includeComplete"
                              color="primary"
                            />
                          )}
                          label="Completed"
                        />
                      </Tooltip>
                      <Tooltip title="Include Deleted Tasks">
                        <FormControlLabel
                          sx={{ m: 0 }}
                          labelPlacement="end"
                          control={(
                            <Checkbox
                              checked={filter.includeArchived}
                              onChange={handleToggleArchived}
                              value="includeArchived"
                              color="primary"
                            />
                          )}
                          label="Deleted"
                        />
                      </Tooltip>
                    </FormGroup>
                  </Grid>
                </Grid>
              </Grid>
            </Collapse>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};
FilterComponent.propTypes = {
  filter: PropTypes.object.isRequired,
  tags: PropTypes.array,
  onClose: PropTypes.func,
  isFetchingEvents: PropTypes.bool,
  includeToday: PropTypes.bool,
  includeThisWeek: PropTypes.bool,
  includeThisMonth: PropTypes.bool,
  includeThisYear: PropTypes.bool,
  onFilter: PropTypes.func,
};
FilterComponent.defaultProps = {
  onClose: () => { },
  onFilter: () => { },
  isFetchingEvents: false,
  includeToday: true,
  includeThisWeek: true,
  includeThisMonth: true,
  includeThisYear: false,
  tags: [],
};
export default (FilterComponent);
